// Storage keys
export const STORAGE_KEYS = {
    AUCTIONS: 'AUCTIONS',
    LOTS: 'LOTS',
    CATEGORIES: 'CATEGORIES',
    CATEGORIES_TIMESTAMP: 'CATEGORIES_TIMESTAMP',
    // Add more keys here as needed
};

// Storage helper functions
export const storage = {
    // Get item from storage with fallback
    get: (key, fallback = null) => {
        try {
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : fallback;
        } catch (error) {
            console.error(`Error reading from localStorage: ${error}`);
            return fallback;
        }
    },

    // Set item in storage
    set: (key, value) => {
        try {
            localStorage.setItem(key, JSON.stringify(value));
            return true;
        } catch (error) {
            console.error(`Error writing to localStorage: ${error}`);
            return false;
        }
    },

    // Remove item from storage
    remove: (key) => {
        try {
            localStorage.removeItem(key);
            return true;
        } catch (error) {
            console.error(`Error removing from localStorage: ${error}`);
            return false;
        }
    },

    // Clear all storage
    clear: () => {
        try {
            localStorage.clear();
            return true;
        } catch (error) {
            console.error(`Error clearing localStorage: ${error}`);
            return false;
        }
    },

    // Update specific fields in stored data
    update: (key, updater) => {
        try {
            const currentData = storage.get(key, {});
            const updatedData = typeof updater === 'function' 
                ? updater(currentData)
                : { ...currentData, ...updater };
            return storage.set(key, updatedData);
        } catch (error) {
            console.error(`Error updating localStorage: ${error}`);
            return false;
        }
    }
};

// Specific storage helpers for auctions
export const auctionStorage = {
    // Get all auctions
    getAuctions: () => storage.get(STORAGE_KEYS.AUCTIONS, []),

    // Save auctions
    saveAuctions: (auctions) => storage.set(STORAGE_KEYS.AUCTIONS, auctions),

    // Add a single auction
    addAuction: (auction) => {
        const auctions = auctionStorage.getAuctions();
        auctions.push(auction);
        return auctionStorage.saveAuctions(auctions);
    },

    // Update a single auction
    updateAuction: (updatedAuction) => {
        const auctions = auctionStorage.getAuctions();
        const index = auctions.findIndex(a => a.id === updatedAuction.id);
        if (index !== -1) {
            auctions[index] = updatedAuction;
            return auctionStorage.saveAuctions(auctions);
        }
        return false;
    },

    // Remove a single auction
    removeAuction: (auctionId) => {
        const auctions = auctionStorage.getAuctions();
        const filteredAuctions = auctions.filter(a => a.id !== auctionId);
        return auctionStorage.saveAuctions(filteredAuctions);
    }
};

// Specific storage helpers for lots
export const lotStorage = {
    // Get all lots
    getLots: () => storage.get(STORAGE_KEYS.LOTS, []),

    // Save lots
    saveLots: (lots) => storage.set(STORAGE_KEYS.LOTS, lots),

    // Add a single lot
    addLot: (lot) => {
        const lots = lotStorage.getLots();
        lots.push(lot);
        return lotStorage.saveLots(lots);
    },

    // Update a single lot
    updateLot: (updatedLot) => {
        const lots = lotStorage.getLots();
        const index = lots.findIndex(l => l.id === updatedLot.id);
        if (index !== -1) {
            lots[index] = updatedLot;
            return lotStorage.saveLots(lots);
        }
        return false;
    },

    // Remove a single lot
    removeLot: (lotId) => {
        const lots = lotStorage.getLots();
        const filteredLots = lots.filter(l => l.id !== lotId);
        return lotStorage.saveLots(filteredLots);
    }
};

// Specific storage helpers for categories
export const categoryStorage = {
    // Cache duration in milliseconds (24 hours)
    CACHE_DURATION: 24 * 60 * 60 * 1000,

    // Get all categories
    getCategories: () => storage.get(STORAGE_KEYS.CATEGORIES, []),

    // Save categories with timestamp
    saveCategories: (categories) => {
        const timestamp = Date.now();
        storage.set(STORAGE_KEYS.CATEGORIES, categories);
        storage.set(STORAGE_KEYS.CATEGORIES_TIMESTAMP, timestamp);
        return true;
    },

    // Check if categories cache is valid
    isCacheValid: () => {
        const timestamp = storage.get(STORAGE_KEYS.CATEGORIES_TIMESTAMP, 0);
        return (Date.now() - timestamp) < categoryStorage.CACHE_DURATION;
    },

    // Get categories if cache is valid, otherwise return empty array
    getCachedCategories: () => {
        if (categoryStorage.isCacheValid()) {
            return categoryStorage.getCategories();
        }
        return [];
    },

    // Clear categories cache
    clearCache: () => {
        storage.remove(STORAGE_KEYS.CATEGORIES);
        storage.remove(STORAGE_KEYS.CATEGORIES_TIMESTAMP);
        return true;
    },

    // Force refresh categories (clear cache)
    forceRefresh: () => {
        return categoryStorage.clearCache();
    }
}; 